import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMoney } from "@/lib/api";
import { PageHeader, StatCard, Loading, StatusPill } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Building2, Users, Wallet, Wrench, TrendingUp, ShieldCheck, CircleDollarSign,
  ArrowRight, BellRing, AlertTriangle,
} from "lucide-react";
import { AnnualReportButton } from "@/components/AnnualReportButton";
import { VacancyPanel } from "@/components/VacancyPanel";

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [maint, setMaint] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/payments", { params: { status: "pending" } }),
      api.get("/maintenance"),
    ]).then(([s, p, m]) => {
      setStats(s.data);
      setPending(p.data);
      setMaint(m.data.filter((x) => x.status !== "completed"));
    });
  }, []);

  if (!stats) return <Loading />;

  const collectPct = stats.tenant_roll > 0
    ? Math.min(100, Math.round(((stats.tenant_roll - stats.tenant_outstanding) / stats.tenant_roll) * 100))
    : 100;

  return (
    <div>
      <PageHeader
        title={`Welcome back`}
        subtitle={`Here's your portfolio snapshot for ${stats.period_label}.`}
        action={
          <div className="flex flex-wrap gap-2">
            <AnnualReportButton />
            <Button onClick={() => navigate("/messages")} data-testid="header-send-reminder" className="gap-2">
              <BellRing className="h-4 w-4" /> Send a notice
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <StatCard testid="stat-properties" label="Properties" value={stats.properties_total}
          hint={`${stats.occupied} occupied · ${stats.vacant} vacant`} icon={Building2} accent="blue" />
        <StatCard testid="stat-tenants" label="Active Tenants" value={stats.tenants_total}
          hint={`${stats.active_leases} active leases`} icon={Users} accent="violet" />
        <StatCard testid="stat-rentroll" label="Monthly Rent Roll" value={fmtMoney(stats.monthly_rent_roll)}
          hint="Gross across all leases" icon={Wallet} accent="primary" />
        <StatCard testid="stat-outstanding" label="Tenant Outstanding" value={fmtMoney(stats.tenant_outstanding)}
          hint={`${stats.period_label} copay due`} icon={CircleDollarSign} accent={stats.tenant_outstanding > 0 ? "rose" : "primary"} />
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-5">
        {/* Section 8 breakdown */}
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6" data-testid="section8-breakdown">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-amber-600" />
              <h2 className="font-display font-bold text-lg text-slate-900">Section 8 vs Tenant Rent Breakdown</h2>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/payments")} className="gap-1 text-primary">
              Ledger <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-amber-700">Housing Authority (HAP)</div>
              <div className="text-2xl font-extrabold font-display text-amber-900 mt-2">{fmtMoney(stats.hap_roll)}</div>
              <div className="text-xs text-amber-700/80 mt-1">Voucher portion · {stats.section8_count} Section 8 lease(s)</div>
            </div>
            <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-indigo-700">Tenant Copay</div>
              <div className="text-2xl font-extrabold font-display text-indigo-900 mt-2">{fmtMoney(stats.tenant_roll)}</div>
              <div className="text-xs text-indigo-700/80 mt-1">Collected directly from tenants</div>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="font-medium text-slate-600">Tenant copay collected · {stats.period_label}</span>
              <span className="font-semibold text-slate-900">{collectPct}%</span>
            </div>
            <Progress value={collectPct} className="h-2.5" />
            <div className="flex items-center gap-1.5 mt-2 text-xs text-emerald-700">
              <TrendingUp className="h-3.5 w-3.5" /> {fmtMoney(stats.collected_this_month)} collected so far this month
            </div>
          </div>
        </div>

        {/* Action items */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-display font-bold text-lg text-slate-900 mb-4">Needs attention</h2>
          <button onClick={() => navigate("/payments")} data-testid="action-pending-payments"
            className="w-full flex items-center gap-3 rounded-xl border border-slate-200 p-3.5 hover:border-primary hover:bg-accent/30 transition mb-3 text-left">
            <span className="h-9 w-9 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center">
              <CircleDollarSign className="h-[18px] w-[18px]" />
            </span>
            <span className="flex-1">
              <span className="block text-sm font-semibold text-slate-800">{stats.pending_confirmations} payment(s) to confirm</span>
              <span className="block text-xs text-slate-500">Tenant-reported payments awaiting review</span>
            </span>
            <ArrowRight className="h-4 w-4 text-slate-400" />
          </button>
          <button onClick={() => navigate("/maintenance")} data-testid="action-open-maintenance"
            className="w-full flex items-center gap-3 rounded-xl border border-slate-200 p-3.5 hover:border-primary hover:bg-accent/30 transition text-left">
            <span className="h-9 w-9 rounded-lg bg-rose-50 text-rose-700 flex items-center justify-center">
              <Wrench className="h-[18px] w-[18px]" />
            </span>
            <span className="flex-1">
              <span className="block text-sm font-semibold text-slate-800">{stats.open_maintenance} open maintenance</span>
              <span className="block text-xs text-slate-500">Requests in progress or unaddressed</span>
            </span>
            <ArrowRight className="h-4 w-4 text-slate-400" />
          </button>

          {stats.tenant_outstanding > 0 && (
            <div className="mt-4 flex items-start gap-2 rounded-xl bg-rose-50 border border-rose-100 p-3.5">
              <AlertTriangle className="h-4 w-4 text-rose-600 mt-0.5" />
              <p className="text-xs text-rose-700 leading-relaxed">
                {fmtMoney(stats.tenant_outstanding)} in tenant copay is still outstanding for {stats.period_label}.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Vacancies + pending + maintenance lists */}
      <div className="grid lg:grid-cols-3 gap-5 mt-5">
        <VacancyPanel vacancies={stats.vacancies} total={stats.vacancy_rent_lost_total} />
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-display font-bold text-lg text-slate-900 mb-4">Payments to confirm</h2>
          {pending.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">All caught up — nothing pending.</p>
          ) : (
            <div className="space-y-2.5">
              {pending.slice(0, 5).map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{p.tenant_name} · {fmtMoney(p.amount)}</div>
                    <div className="text-xs text-slate-500 capitalize">{p.method} · {p.property_name}</div>
                  </div>
                  <StatusPill status="pending" />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-display font-bold text-lg text-slate-900 mb-4">Open maintenance</h2>
          {maint.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">No open requests.</p>
          ) : (
            <div className="space-y-2.5">
              {maint.slice(0, 5).map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{m.title}</div>
                    <div className="text-xs text-slate-500">{m.property_name} · {m.tenant_name}</div>
                  </div>
                  <StatusPill status={m.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
